import { FetchPolicy, TypedDocumentNode, useApolloClient } from '@apollo/client'
import {
  FiltersDefinition,
  FiltersState,
  RecordType,
  SortOrder,
  SortingsDefinition,
  SortingsState,
} from '@factorialco/f0-react'
import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import { DEFAULT_PAGE_SIZE, PaginationVars } from './useGraphqlDataSource'

interface UseGraphqlChildrenDataCollectionProps<
  Record extends RecordType,
  Filters extends FiltersDefinition,
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>>,
> {
  query: Document
  mapVariables: (params: {
    item: Record
    filters?: FiltersState<Filters>
    pagination?: PaginationInfo
    sortOrder?: SortOrder
  }) => Omit<VariablesOf<Document>, keyof PaginationVars>
  mapData: (params: { data: ResultOf<Document>; item: Record }) => Array<Record>
  totalCount: (data: ResultOf<Document>) => number
  fetchPolicy?: FetchPolicy
  perPage?: number
  childrenType?: 'detailed' | 'basic'
}

export type ChildrenPaginationInfo = {
  total: number
  perPage: number
  currentPage: number
  pagesCount: number
  hasMore: boolean
}

export interface PaginationInfo {
  currentPage: number
  perPage: number
}

interface FetchChildrenParams<Record extends RecordType, Filters extends FiltersDefinition> {
  item: Record
  filters?: FiltersState<Filters>
  pagination?: PaginationInfo
  sortings?: SortingsState<SortingsDefinition>
}

export const useGraphqlChildrenDataCollection = <
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>>,
  Record extends RecordType,
  Filters extends FiltersDefinition,
>({
  query,
  mapVariables,
  mapData,
  totalCount,
  childrenType,
  fetchPolicy = 'network-only',
  perPage = DEFAULT_PAGE_SIZE,
}: UseGraphqlChildrenDataCollectionProps<Record, Filters, Document>) => {
  const client = useApolloClient()

  const fetchChildren = useUnsafeCallback(
    ({ item, filters, pagination, sortings }: FetchChildrenParams<Record, Filters>) => {
      const paginationInfo = pagination
        ? {
            perPage: pagination.perPage,
            currentPage: pagination.currentPage + 1,
          }
        : {
            perPage: perPage,
            currentPage: 1,
          }

      const paginationVars: {
        perPage: number
        page?: number
      } = {
        perPage: paginationInfo.perPage ?? perPage,
        page: paginationInfo.currentPage ?? 1,
      }

      const observable = client
        .watchQuery({
          query,
          fetchPolicy,
          variables: {
            ...mapVariables({ item, filters }),
            ...paginationVars,
            sortOrder: sortings,
          },
          notifyOnNetworkStatusChange: false,
          nextFetchPolicy: 'cache-and-network',
        })
        .map(({ data, loading, error }) => {
          const mappedData = mapData({ data, item })

          const total = totalCount(data)

          const response = {
            records: mappedData,
            type: childrenType,
            paginationInfo: {
              cursor: null,
              total: total,
              perPage: perPage,
              currentPage: paginationInfo.currentPage,
              pagesCount: Math.ceil(total / perPage),
              hasMore: paginationInfo.currentPage * paginationInfo.perPage < total,
            },
          }

          return {
            loading,
            error,
            data: response,
          }
        })
      return observable
    },
    [client, query, mapVariables, mapData, totalCount, fetchPolicy, perPage, childrenType]
  )

  return {
    fetchChildren,
  }
}
